import { IBayPattern, ILashingBridgeGeometry } from "@tedivo/tedivo-bay-grid-pure";

import { roundDec } from "@tedivo/tedivo-pure-helpers";

const MIN_WIDTH_PX = 6;
const MIN_POST_PX = 1.5;
const MIN_BAR_PX = 1;

/** Sizes of the members, as ratios of the tier height (the same as the 3D view) */
const POST_RATIO = 0.08;
const BAR_RATIO = 0.04;

/**
 * Draws the lashing bridges in the side view, like the 3D view seen from the side: a post on
 * each face (fore and aft) up to the top walkway, a bar at every walkway, and over the top
 * walkway the thick stanchions (from each face to the clear walkway) and the handrails. The side view is a profile, so
 * it draws the frame of the tallest row, from the lowest stack bottom.
 * @returns The group and the topmost Y drawn (SVG coordinates), to grow the viewBox
 */
export function drawSideLashingBridges({
  geometries,
  xPos,
  yPos,
  paddingY,
  xRange,
  getOffsetMm,
  color,
  strokeColor = color,
}: {
  geometries: ILashingBridgeGeometry[];
  xPos: (lcg: number, d?: number) => number;
  yPos: (vcg: number, d?: number) => number;
  /** `svgElementMeasures.paddingShip.y`, added to every Y like the containers */
  paddingY: number;
  xRange: number;
  /** Bottom base offset, in mm, of the above-deck level of the bay */
  getOffsetMm: (isoBay: IBayPattern) => number;
  /** Fill color */
  color: string;
  /** Outline color, so a light fill (e.g. white) stays visible. Defaults to `color` */
  strokeColor?: string;
}): { group: SVGGElement; topY: number } {
  const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
  group.setAttribute("id", "LashingBridges");

  /** Pixels per mm, vertically */
  const pxPerMm = Math.abs(yPos(0) - yPos(1000)) / 1000;

  let topY = Infinity;

  geometries.forEach((g) => {
    const off = getOffsetMm(g.isoBay);
    const y = (mm: number) => yPos(mm + off) + paddingY;

    const xA = xPos(g.lcgCenter - g.thickness * 0.5);
    const xB = xPos(g.lcgCenter + g.thickness * 0.5);
    const xMid = (xA + xB) * 0.5;

    // Removes bugs when the bridge is outside the ship
    if (!xRange || Math.abs(xMid / xRange) > 1.5) return;

    const width = Math.max(Math.abs(xB - xA), MIN_WIDTH_PX);
    const xLeft = xMid - width * 0.5;
    const post = Math.min(Math.max(g.tierHeight * POST_RATIO * pxPerMm, MIN_POST_PX), width / 3);
    const bar = Math.max(g.tierHeight * BAR_RATIO * pxPerMm, MIN_BAR_PX);

    // The tallest row gives the profile
    const profile = g.rows.reduce((a, b) => (b.structureTop > a.structureTop ? b : a));
    const topWalkway = profile.walkways[profile.walkways.length - 1] ?? profile.structureTop;

    // Over the top walkway, the stanchions fill the depth from each face to the clear walkway
    const clearWidth =
      g.thickness > 0 ? Math.min(width, (width * g.topWalkwayDepth) / g.thickness) : width;
    const stanchion = clearWidth < width ? Math.max((width - clearWidth) * 0.5, MIN_POST_PX) : post;

    const yTop = y(g.maxStructureTop);
    const yTopWalkway = y(topWalkway);
    const yBottom = y(g.minBottomBase);

    const rects: Array<[number, number, number, number]> = [
      // Posts on both faces, up to the top walkway
      [xLeft, yTopWalkway, post, yBottom - yTopWalkway],
      [xLeft + width - post, yTopWalkway, post, yBottom - yTopWalkway],
      // Stanchions over the top walkway
      [xLeft, yTop, stanchion, yTopWalkway - yTop],
      [xLeft + width - stanchion, yTop, stanchion, yTopWalkway - yTop],
      // Walkways, drawn downwards from their level (inside the tier below it)
      ...profile.walkways.map((w): [number, number, number, number] => [xLeft, y(w), width, bar]),
      // Top and middle handrails
      [xLeft, yTop, width, bar],
      [xLeft, y((topWalkway + profile.structureTop) * 0.5) - bar * 0.5, width, bar],
    ];

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      rects
        .map(([rx, ry, rw, rh]) => {
          const [x0, y0, w0, h0] = [rx, ry, rw, Math.max(rh, 0)].map((v) => roundDec(v, 1));
          return `M${x0},${y0} h${w0} v${h0} h${-w0} z`;
        })
        .join(" "),
    );
    path.setAttribute("fill", color);
    path.setAttribute("stroke", strokeColor);
    path.setAttribute("stroke-width", "0.5");
    path.setAttribute("data-type", "lashingBridge");
    path.setAttribute("data-bay", g.isoBay);
    path.setAttribute("data-side", g.side);

    const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
    title.textContent = `${g.isoBay} ${g.side === "fore" ? "FWD" : "AFT"}`;
    path.appendChild(title);

    group.appendChild(path);
    topY = Math.min(topY, yTop);
  });

  return { group, topY };
}

/** Bottom base offset of the above-deck level of a bay, from `offsetBottomBasesDict` (keyed `${bays}-${level}`) */
export function getAboveOffsetForBay(
  offsetBottomBasesDict: Record<string, number>,
  isoBay: IBayPattern,
  aboveLevel: number,
): number {
  const key = Object.keys(offsetBottomBasesDict).find((k) => {
    const parts = k.split("-");
    const level = parts.pop();
    return level === String(aboveLevel) && parts.includes(isoBay);
  });

  return key !== undefined ? (offsetBottomBasesDict[key] ?? 0) : 0;
}
